import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Loader2 } from "lucide-react";

export default function SupportDialog({ open, onOpenChange, embedded = false, onSubmitted }) {
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("Name and phone are required");
      return;
    }
    setBusy(true);
    try {
      await api.post("/supporters", {
        name: form.name,
        phone: form.phone,
        email: form.email || null,
        message: form.message,
      });
      toast.success("Thank you for your support!");
      setForm({ name: "", phone: "", email: "", message: "" });
      if (onSubmitted) onSubmitted();
      if (onOpenChange) onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const FormBody = (
    <form onSubmit={submit} className="space-y-4" data-testid="support-form">
      <div>
        <Label htmlFor="s-name" className="uppercase text-[10px] tracking-[0.2em]">Full Name *</Label>
        <Input id="s-name" data-testid="support-name-input"
          value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
          required placeholder="Your name" />
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="s-phone" className="uppercase text-[10px] tracking-[0.2em]">Phone *</Label>
          <Input id="s-phone" data-testid="support-phone-input"
            value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
            required placeholder="+91-98XXXXXXXX" />
        </div>
        <div>
          <Label htmlFor="s-email" className="uppercase text-[10px] tracking-[0.2em]">Email</Label>
          <Input id="s-email" type="email" data-testid="support-email-input"
            value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="optional" />
        </div>
      </div>
      <div>
        <Label htmlFor="s-message" className="uppercase text-[10px] tracking-[0.2em]">Suggestion / Message</Label>
        <Textarea id="s-message" data-testid="support-message-input"
          value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
          placeholder="Share your thoughts..." rows={3} />
      </div>
      <Button type="submit" disabled={busy} data-testid="support-submit-btn"
        className="w-full bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent-hover))] text-white uppercase tracking-widest rounded-none">
        {busy && <Loader2 size={16} className="mr-2 animate-spin" />}
        Pledge Support
      </Button>
    </form>
  );

  if (embedded) return FormBody;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg rounded-none border-2 border-[hsl(var(--primary))]">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl tracking-wide">Stand With Dr. Garg</DialogTitle>
          <DialogDescription>
            Pledge your support — every voice strengthens the movement.
          </DialogDescription>
        </DialogHeader>
        {FormBody}
      </DialogContent>
    </Dialog>
  );
}
